import type {
  AeropayBankAccount,
  CheckoutProps,
  KioskUser,
  CheckoutResponse as V3CheckoutResponse,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useCart,
  useCheckoutRequirements,
  useStoreSpecials,
  useCheckout as useV3CheckoutMutation,
  useUpdateCart as useV3UpdateCart,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useMfa } from '@iheartjane/menu';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import type {
  CreateCheckoutParams,
  RootLoaderType,
  CheckoutResponse,
} from '@jane/kiosk/data-access';
import {
  updateLocalCartUuid,
  useGetJanePayCart,
  useGetKiosk,
  useKioskAeropayUser,
  useCheckoutRefusal,
  useKioskUser,
  useKioskUserLogout,
  useLocalCartUuid,
  useRemoveUserFromCart,
  useSubmitOrder,
} from '@jane/kiosk/data-access';
import {
  useKioskCart,
  useShowJanePayOnKiosk,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useCartInfoForTracking } from '@jane/kiosk/providers/lib/CartInfoForTrackingProvider';
import { useLogoutUser } from '@jane/kiosk/providers/lib/hooks/useLogoutUser';
import { useIdleContext } from '@jane/kiosk/providers/lib/IdleProvider';
import { useProductTracking } from '@jane/kiosk/providers/lib/ProductTrackingProvider';
import { useSessionContext } from '@jane/kiosk/providers/lib/SessionProvider';
import {
  areRequiredConsentsValid,
  buildKioskCheckoutProps,
  formatBirthdate,
  resetSignInPrompt,
  toKioskConsentItems,
  toUserConsentPayload,
  trackLinkBankAccountTap,
  trackOrderSubmitted,
  trackSignedOut,
} from '@jane/kiosk/util';
import type { KioskAgreementLink, KioskConsentItem } from '@jane/kiosk/util';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { getJwt } from '@jane/shared/auth';
import type {
  Cart,
  EmptyCart,
  Kiosk,
  Store,
  StoreSpecial,
} from '@jane/shared/types';
import { formatTime } from '@jane/shared/util';

export const SCREEN_NAMES = {
  agreementQrCode: 'agreement-qr-code',
  agreements: 'agreements',
  customerInfo: 'customer-info',
  linkBankCallout: 'link-bank-callout',
  linkBankPayment: 'link-bank-payment',
  loginCallout: 'login-callout',
  loginPayment: 'login-payment',
  loading: 'loading',
  mfa: 'mfa',
  payment: 'payment',
};

export type CheckoutFormFields = {
  'b-day-search': number;
  'checkout-notes'?: string;
  code?: string;
  'contact-num-search': string;
  'name-f-search': string;
  'name-l-search': string;
  'name-li-search': string;
};
export interface CheckoutContextType {
  acceptAgreements(): void;
  agreementLinkInView: KioskAgreementLink | null;
  agreementsError: boolean;
  agreementsLoading: boolean;
  anonymousCheckout: boolean;
  bankAccounts: AeropayBankAccount[];
  cart: Cart | EmptyCart;
  checkoutAgreements: KioskConsentItem[];
  codeResent: boolean;
  consents: Record<string, boolean>;
  error: string;
  errorDetail?: string;
  errorSendingCode: string | null;
  errorVerifyingCode: string | null;
  formMethods: UseFormReturn<CheckoutFormFields>;
  handleContinueToMFAClick(): void;
  handleLinkBankClick(): void;
  handleLogout(): void;
  handleSubmit(): void;
  handleSubmitWithValidation(): void;
  isOrderLoading: boolean;
  janePayCartLoading: boolean;
  janePayOrderTotal: number;
  janePayServiceFeeAmount: number;
  kiosk: Kiosk | null;
  kioskAgreementsEnabled: boolean;
  kioskUser: KioskUser | null;
  reopenAgreements(): void;
  requiredConsentsValid: boolean;
  resendCode(): void;
  screen: string;
  selectedPayment: string;
  sendingCode: boolean;
  setAgreementLinkInView: (link: KioskAgreementLink | null) => void;
  setAnonymousCheckout(anon: boolean): void;
  setConsents: Dispatch<SetStateAction<Record<string, boolean>>>;
  setError(error: string): void;
  setErrorDetail(detail: string | undefined): void;
  setScreen(screen: string): void;
  setSelectedPayment: (payment: string) => void;
  store: Store;
  userPhone: string;
  verifyCode(code: string): Promise<void>;
  verifyingCode: boolean;
}

export const CheckoutContext = createContext<CheckoutContextType | null>(null);
CheckoutContext.displayName = 'Checkout';

export const CheckoutProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { jdid } = useSessionContext();
  const { setTimerDisabled } = useIdleContext();
  const { setCartInfo } = useCartInfoForTracking();
  const {
    kiosk: initialKiosk,
    kioskId,
    storeId,
    store,
  } = useTypedRouteLoaderData<RootLoaderType>('root');

  const {
    canSendCode,
    codeResent,
    errorSendingCode,
    errorVerifyingCode,
    resendCode,
    sendCode,
    sendingCode,
    verifyCode,
    verifyingCode,
  } = useMfa();

  /** STATE CONTROLLERS */
  const [screenState, setScreen] = useState(SCREEN_NAMES.customerInfo);
  const [agreementsAccepted, setAgreementsAccepted] = useState(false);
  const [agreementLinkInView, setAgreementLinkInView] =
    useState<KioskAgreementLink | null>(null);
  const [anonymousCheckout, setAnonymousCheckout] = useState(
    store.kiosk_anonymous_checkout === 'required'
  );
  const [selectedPayment, setSelectedPayment] = useState('link');
  const [error, setError] = useState('');
  // CartCheckoutError maps `error` to Aeropay copy only, so our copy travels separately.
  const [errorDetail, setErrorDetail] = useState<string | undefined>();
  const handleCheckoutRefusal = useCheckoutRefusal();

  /** DATA ACCESS */
  const { data: kiosk } = useGetKiosk({
    kioskId,
    initialData: initialKiosk,
  });

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const {
    cartToppers: { cartTopperIds },
    inlineAds: { inlineAdIds },
  } = useProductTracking();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const cartUuid = useLocalCartUuid();
  const { data: cart } = useKioskCart();
  const showJanePay = useShowJanePayOnKiosk();
  // v3 keys the Jane-Pay-optimized cart on `paymentMethod: 'jane_pay'` so it
  // doesn't collide with the regular cart's `['cart', uuid]` key. The v1
  // equivalent below is namespaced separately again (see `v1CartKeys`).
  const { data: v3JanePayCart, isFetching: isV3JanePayFetching } = useCart(
    { appMode: 'kiosk', uuid: cartUuid ?? '', paymentMethod: 'jane_pay' },
    { enabled: !!useV3Cart && showJanePay && !!cartUuid }
  );
  const { data: v1JanePayCart = {}, isFetching: isV1JanePayFetching } =
    useGetJanePayCart(cartUuid, {
      enabled: !useV3Cart && showJanePay,
    });
  const janePayCart = useV3Cart ? v3JanePayCart : v1JanePayCart;
  const janePayCartLoading = useV3Cart
    ? isV3JanePayFetching
    : isV1JanePayFetching;
  const { data: kioskUser } = useKioskUser();
  const { data: aeropayData } = useKioskAeropayUser(
    {
      janeUserId: kioskUser?.id,
    },
    {
      // refetch for aeropay data to check for linked bank accounts
      refetchInterval:
        screenState.includes('link-bank') || screenState.includes('login')
          ? 5000
          : false,
    }
  );

  /** CHECKOUT AGREEMENTS */
  // Defaulted: an undefined flag (LD not resolved yet) would leave `enabled`
  // undefined below, which react-query reads as enabled.
  const kioskAgreementsEnabled = useFlag<boolean>(
    FLAGS.c1KioskAgreements,
    false
  );
  // jwt is optional — guests see agreements too. userId only keys the cache: the
  // response drops agreements the signed-in user already accepted, and a shared
  // terminal must not hand that trimmed list to the next shopper.
  const {
    data: checkoutRequirements,
    isLoading: agreementsLoading,
    isError: agreementsError,
  } = useCheckoutRequirements(
    {
      storeId,
      reservationMode: 'kiosk',
      appMode: 'kiosk',
      jwt: getJwt(),
      userId: kioskUser?.id ?? null,
    },
    { enabled: kioskAgreementsEnabled && !!storeId }
  );
  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const checkoutAgreements = useMemo<KioskConsentItem[]>(
    () =>
      toKioskConsentItems(checkoutRequirements?.checkout_agreement_versions),
    [checkoutRequirements]
  );
  const requiredConsentsValid = useMemo(
    () => areRequiredConsentsValid(checkoutAgreements, consents),
    [checkoutAgreements, consents]
  );
  const userConsentPayload = useMemo(
    () => toUserConsentPayload(checkoutAgreements, consents),
    [checkoutAgreements, consents]
  );
  // Accepting the store's terms gates the rest of checkout: until the shopper
  // taps through, the agreements step (or the QR code page for a link they
  // opened) replaces whatever screen the flow is on. Derived rather than seeded
  // into `screen`, because the flag and the requirements both resolve after
  // mount — an initial screen captured up front would skip the step entirely.
  // A store with no agreements has nothing to accept, so the gate never opens.
  // Loading/errored counts as needing the step: failing closed keeps a slow or
  // failed load from letting a shopper past consent.
  const agreementsPending =
    kioskAgreementsEnabled &&
    !agreementsAccepted &&
    (agreementsLoading || agreementsError || checkoutAgreements.length > 0);
  const screen = agreementsPending
    ? agreementLinkInView
      ? SCREEN_NAMES.agreementQrCode
      : SCREEN_NAMES.agreements
    : screenState;

  const acceptAgreements = useCallback(() => {
    setAgreementLinkInView(null);
    setAgreementsAccepted(true);
  }, []);

  /** Re-gates checkout on consent, e.g. after a logout clears the consents. */
  const reopenAgreements = useCallback(() => {
    setAgreementLinkInView(null);
    setAgreementsAccepted(false);
  }, []);
  /**
   * After successfully checking out, the user needs to be logged out from the
   * kiosk, but we do not want to remove their user ID from their cart. In this
   * case, we use the logout mutation directly instead of the hook. If the user
   * logs out while they are checking out, we use the logout function from the
   * hook (renamed logoutAndRemoveCartUser for clarity).
   */
  const { mutate: logout } = useKioskUserLogout({});
  const { logout: logoutAndRemoveCartUser } = useLogoutUser();
  const { mutate: removeUser } = useRemoveUserFromCart();
  const { mutate: v3RemoveUser } = useV3UpdateCart({});

  /** FORM CONTEXT */
  const userFirstName = kioskUser?.first_name;
  const userLastName = kioskUser?.last_name;
  const userLastInitial = userLastName?.slice(0, 1);
  const userPhone = kioskUser?.phone;
  const userBirthDate = kioskUser?.birth_date
    ? formatTime({
        time: kioskUser?.birth_date,
        outputFormat: 'MMddyyyy',
        inputFormat: 'yyyy-M-d',
      })
    : undefined;

  const defaultValues = useMemo(
    () =>
      kioskUser
        ? {
            'name-f-search': userFirstName ?? undefined,
            'name-l-search': userLastName ?? undefined,
            'name-li-search': userLastInitial ?? undefined,
            'contact-num-search': userPhone ?? '',
            'b-day-search': (userBirthDate as unknown as number) ?? undefined,
          }
        : undefined,
    [
      kioskUser,
      userFirstName,
      userLastName,
      userLastInitial,
      userPhone,
      userBirthDate,
    ]
  );
  const formMethods = useForm<CheckoutFormFields>({
    mode: 'onSubmit',
    defaultValues,
  });

  /** DEFINITIONS */
  const strictAnonymousCheckout = Boolean(
    store.kiosk_strict_anonymous_checkout
  );

  const bankAccounts = useMemo(
    () => aeropayData?.bank_accounts ?? [],
    [aeropayData]
  );

  const payingWithJanePay = useMemo(
    () =>
      bankAccounts.find(
        (account) => account.account_last_4 === selectedPayment
      ),
    [bankAccounts, selectedPayment]
  );

  const dmTypesInCart = useMemo(() => {
    const dmTypes = [];

    if (cartTopperIds.length > 0) dmTypes.push('bag');
    if (inlineAdIds.length > 0) dmTypes.push('in-line');

    return dmTypes;
  }, [cartTopperIds, inlineAdIds]);

  /** METHODS */
  const onOrderError = useCallback(
    (error: unknown, { birth_date, message, phone }: CreateCheckoutParams) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate:
            anonymousCheckout || kiosk?.disable_birth_date_at_checkout
              ? null
              : birth_date,
          checkoutError: `${error}`,
          message: message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : phone,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod:
          payingWithJanePay && !anonymousCheckout ? 'jane pay' : null,
      });

      setErrorDetail(handleCheckoutRefusal(error));
      setError(String(error));
      setScreen(
        showJanePay && !anonymousCheckout
          ? SCREEN_NAMES.payment
          : SCREEN_NAMES.customerInfo
      );
      setTimerDisabled(false);
    },
    [
      cart,
      kiosk,
      specials,
      store,
      anonymousCheckout,
      dmTypesInCart,
      payingWithJanePay,
      showJanePay,
      setTimerDisabled,
      handleCheckoutRefusal,
    ]
  );

  const onOrderSuccess = useCallback(
    async (
      { reservation: { id, queue_number } }: CheckoutResponse,
      { birth_date, message, phone }: CreateCheckoutParams
    ) => {
      const phoneNumber =
        anonymousCheckout || kiosk?.disable_phone_number_at_checkout
          ? null
          : phone;
      const birthDate =
        anonymousCheckout || kiosk?.disable_birth_date_at_checkout
          ? null
          : birth_date;
      trackOrderSubmitted({
        cart,
        kiosk,
        store,
        specials,
        trackingBag: {
          birthDate: birth_date,
          checkoutError: null,
          message: message || null,
          phoneNumber,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod:
          payingWithJanePay && !anonymousCheckout ? 'jane pay' : null,
      });
      const selectedBankAccount =
        bankAccounts.length > 0 && !anonymousCheckout
          ? bankAccounts.find(
              (account) => account.account_last_4 === selectedPayment
            )?.name ||
            bankAccounts.find((account) => account.is_selected)?.name ||
            bankAccounts[0].name
          : null;
      setCartInfo({
        cart,
        birthDate,
        phoneNumber,
        selectedBankAccount,
        showPayByLink: Boolean(
          !selectedBankAccount && showJanePay && store.kiosk_pay_by_link_enabled
        ),
        signedInOrder: !!kioskUser,
      });

      //storing the queue number in local storage to ensure that it persists on the Order Checkout page
      localStorage.setItem('queueNumber', queue_number);

      if (kioskUser) {
        logout();
      }

      setConsents({});
      navigate(`/stores/${storeId}/reservation/${id}/order-complete`);
      setTimerDisabled(false);
      resetSignInPrompt();
    },
    [
      anonymousCheckout,
      kiosk,
      cart,
      store,
      specials,
      dmTypesInCart,
      payingWithJanePay,
      showJanePay,
      setCartInfo,
      bankAccounts,
      kioskUser,
      navigate,
      storeId,
      setTimerDisabled,
      selectedPayment,
      logout,
    ]
  );

  const { mutate: submitOrder, isPending } = useSubmitOrder({
    onError: onOrderError,
    onSuccess: onOrderSuccess,
  });

  const onV3OrderError = useCallback(
    (error: unknown, variables: CheckoutProps) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate:
            anonymousCheckout || kiosk?.disable_birth_date_at_checkout
              ? null
              : (variables.ageVerification?.birth_date ?? null),
          checkoutError: `${error}`,
          message: variables.message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : (variables.customer.phone ?? null),
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod:
          payingWithJanePay && !anonymousCheckout ? 'jane pay' : null,
      });

      setErrorDetail(handleCheckoutRefusal(error));
      setError(String(error));
      setScreen(
        showJanePay && !anonymousCheckout
          ? SCREEN_NAMES.payment
          : SCREEN_NAMES.customerInfo
      );
      setTimerDisabled(false);
    },
    [
      cart,
      kiosk,
      specials,
      store,
      anonymousCheckout,
      dmTypesInCart,
      payingWithJanePay,
      showJanePay,
      setTimerDisabled,
      handleCheckoutRefusal,
    ]
  );

  const onV3OrderSuccess = useCallback(
    async (
      { reservation: { id, queue_number } }: V3CheckoutResponse,
      variables: CheckoutProps
    ) => {
      const birth_date = variables.ageVerification?.birth_date;
      const phone = variables.customer.phone;
      const phoneNumber =
        anonymousCheckout || kiosk?.disable_phone_number_at_checkout
          ? null
          : (phone ?? null);
      const birthDate =
        anonymousCheckout || kiosk?.disable_birth_date_at_checkout
          ? null
          : (birth_date ?? null);
      trackOrderSubmitted({
        cart,
        kiosk,
        store,
        specials,
        trackingBag: {
          birthDate: birth_date ?? null,
          checkoutError: null,
          message: variables.message || null,
          phoneNumber,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod:
          payingWithJanePay && !anonymousCheckout ? 'jane pay' : null,
      });
      const selectedBankAccount =
        bankAccounts.length > 0 && !anonymousCheckout
          ? bankAccounts.find(
              (account) => account.account_last_4 === selectedPayment
            )?.name ||
            bankAccounts.find((account) => account.is_selected)?.name ||
            bankAccounts[0].name
          : null;
      setCartInfo({
        cart,
        birthDate,
        phoneNumber,
        selectedBankAccount,
        showPayByLink: Boolean(
          !selectedBankAccount && showJanePay && store.kiosk_pay_by_link_enabled
        ),
        signedInOrder: !!kioskUser,
      });

      //storing the queue number in local storage to ensure that it persists on the Order Checkout page
      localStorage.setItem('queueNumber', String(queue_number ?? ''));

      updateLocalCartUuid(null);

      if (kioskUser) {
        logout();
      }

      setConsents({});
      navigate(`/stores/${storeId}/reservation/${id}/order-complete`);
      setTimerDisabled(false);
      resetSignInPrompt();
    },
    [
      anonymousCheckout,
      kiosk,
      cart,
      store,
      specials,
      dmTypesInCart,
      payingWithJanePay,
      showJanePay,
      setCartInfo,
      bankAccounts,
      kioskUser,
      navigate,
      storeId,
      setTimerDisabled,
      selectedPayment,
      logout,
    ]
  );

  const { mutate: v3Checkout, isPending: isV3Loading } = useV3CheckoutMutation({
    onError: onV3OrderError,
    onSuccess: onV3OrderSuccess,
  });

  const handleSubmit = useCallback(async () => {
    const [message, phone, firstName, lastName, lastInitial, birthDate] =
      formMethods.getValues([
        'checkout-notes',
        'contact-num-search',
        'name-f-search',
        'name-l-search',
        'name-li-search',
        'b-day-search',
      ]);

    setScreen(SCREEN_NAMES.loading);
    setTimerDisabled(true);
    //if the user is checking out anonymously, we update their cart to remove their user id
    if (!!kioskUser && strictAnonymousCheckout && anonymousCheckout) {
      if (useV3Cart) {
        v3RemoveUser({ appMode: 'kiosk', uuid: cart.uuid, userId: null });
      } else {
        removeUser({ uuid: cart.uuid });
      }
    }

    const dateOfBirth =
      anonymousCheckout || kiosk?.disable_birth_date_at_checkout
        ? null
        : userBirthDate
          ? formatBirthdate(userBirthDate)
          : birthDate
            ? formatBirthdate(birthDate)
            : undefined;

    const name =
      strictAnonymousCheckout && anonymousCheckout
        ? 'ANONYMOUS CHECKOUT'
        : [
            userFirstName || firstName,
            userLastName || lastName || lastInitial,
          ].join(' ');

    const phoneNumber =
      anonymousCheckout || kiosk?.disable_phone_number_at_checkout
        ? ''
        : userPhone || phone;

    if (useV3Cart) {
      v3Checkout(
        buildKioskCheckoutProps({
          cartUuid: cart.uuid,
          name,
          phone: phoneNumber,
          birthDate: dateOfBirth ?? undefined,
          message,
          deviceId: jdid,
          nowSeconds: Math.floor(Date.now() / 1000).toString(),
          janePay:
            payingWithJanePay && !anonymousCheckout
              ? {
                  bankAccountId: payingWithJanePay.bank_account_id,
                  accountVerified: bankAccounts.length > 0,
                }
              : null,
          jwt: getJwt(),
          userConsent: kioskAgreementsEnabled ? userConsentPayload : undefined,
        })
      );
      return;
    }

    submitOrder({
      aeropay_account_verified: bankAccounts.length > 0,
      birth_date: dateOfBirth ?? undefined,
      cart_uuid: cart.uuid,
      current_url: window.location.href,
      j_device_id: jdid,
      message,
      name,
      phone: phoneNumber,
      // v1 submit path (reached only when `useV3Cart` is false): `cart` is the
      // v1 cart here, so narrow to read its v1 `products` shape.
      products: (cart as Cart).products,
      store_id: storeId,
      ...(kioskAgreementsEnabled &&
        userConsentPayload.length > 0 && {
          user_consent: userConsentPayload,
        }),
      ...(payingWithJanePay &&
        !anonymousCheckout && {
          payment_method: 'jane_pay',
          aeropay_bank_account_id: payingWithJanePay.bank_account_id ?? null,
        }),
    });
  }, [
    anonymousCheckout,
    bankAccounts.length,
    cart,
    formMethods,
    jdid,
    kiosk?.disable_birth_date_at_checkout,
    kiosk?.disable_phone_number_at_checkout,
    kioskAgreementsEnabled,
    kioskUser,
    payingWithJanePay,
    removeUser,
    v3RemoveUser,
    setTimerDisabled,
    storeId,
    strictAnonymousCheckout,
    submitOrder,
    useV3Cart,
    userConsentPayload,
    v3Checkout,
    userBirthDate,
    userFirstName,
    userLastName,
    userPhone,
  ]);

  const handleSubmitWithValidation = async () => {
    const isValid = await formMethods.trigger();

    if (isValid) {
      handleSubmit();
    }
  };

  const handleLogout = () => {
    setSelectedPayment('link');
    setConsents({});
    // Consents just got cleared, so the shopper has to accept them again.
    reopenAgreements();
    trackSignedOut({ source: ['Checkout'], store });
    logoutAndRemoveCartUser();
    formMethods.reset({
      'name-f-search': '',
      'name-l-search': '',
      'name-li-search': '',
      'contact-num-search': '',
      'b-day-search': '' as unknown as number,
    });
  };

  const handleLinkBankClick = () => {
    trackLinkBankAccountTap({
      deviceType: 'Kiosk',
      source: ['Checkout'],
      store,
    });

    if (!kioskUser) {
      setScreen(
        screen === SCREEN_NAMES.payment
          ? SCREEN_NAMES.loginPayment
          : SCREEN_NAMES.loginCallout
      );
    } else {
      setScreen(
        screen === SCREEN_NAMES.payment
          ? SCREEN_NAMES.linkBankPayment
          : SCREEN_NAMES.linkBankCallout
      );
    }
    setTimerDisabled(true);
  };

  const handleContinueToMFAClick = async () => {
    if (canSendCode()) sendCode();
    setTimerDisabled(true);
    setScreen(SCREEN_NAMES.mfa);
  };

  const handleVerifyCode = useCallback(
    async (code: string) => {
      const success = await verifyCode(code);
      if (success) {
        setTimerDisabled(false);
        handleSubmit();
      }
    },
    [verifyCode, setTimerDisabled, handleSubmit]
  );

  useEffect(() => {
    if (defaultValues && formMethods) {
      formMethods.reset(defaultValues);
    }
  }, [defaultValues, formMethods]);

  // Jane Pay (ACH) fee + fee-inclusive total from the Jane-Pay-optimized cart.
  // Falls back to the base cart total while the Jane Pay cart loads / when the
  // store does not pass the fee.
  const janePayServiceFeeAmount = janePayCart?.jane_pay_service_fee_amount ?? 0;
  const janePayOrderTotal = janePayCart?.order_total ?? cart?.order_total ?? 0;

  const context = useMemo(
    () => ({
      acceptAgreements,
      agreementLinkInView,
      agreementsError,
      agreementsLoading,
      anonymousCheckout,
      bankAccounts,
      // CheckoutContextType.cart is still the v1 `Cart | EmptyCart` contract;
      // narrow the v1∪v3 union to match it.
      cart: cart as Cart | EmptyCart,
      checkoutAgreements,
      consents,
      janePayCartLoading,
      janePayOrderTotal,
      janePayServiceFeeAmount,
      codeResent,
      error,
      errorDetail,
      errorSendingCode,
      errorVerifyingCode,
      formMethods,
      handleContinueToMFAClick,
      handleLinkBankClick,
      handleLogout,
      handleSubmit,
      isOrderLoading: useV3Cart ? isV3Loading : isPending,
      kiosk: kiosk ?? null,
      kioskAgreementsEnabled,
      kioskUser: kioskUser ?? null,
      reopenAgreements,
      requiredConsentsValid,
      resendCode,
      screen,
      selectedPayment,
      sendingCode,
      setAgreementLinkInView,
      setAnonymousCheckout,
      setConsents,
      setError,
      setErrorDetail,
      setScreen,
      setSelectedPayment,
      store,
      handleSubmitWithValidation,
      userPhone: userPhone ?? '',
      verifyCode: handleVerifyCode,
      verifyingCode,
    }),
    [
      acceptAgreements,
      agreementLinkInView,
      agreementsError,
      agreementsLoading,
      anonymousCheckout,
      bankAccounts,
      cart,
      checkoutAgreements,
      consents,
      codeResent,
      error,
      errorDetail,
      errorSendingCode,
      errorVerifyingCode,
      formMethods,
      handleContinueToMFAClick,
      handleLinkBankClick,
      handleLogout,
      handleSubmit,
      isPending,
      isV3Loading,
      useV3Cart,
      janePayCartLoading,
      janePayOrderTotal,
      janePayServiceFeeAmount,
      kiosk,
      kioskAgreementsEnabled,
      kioskUser,
      reopenAgreements,
      requiredConsentsValid,
      resendCode,
      screen,
      selectedPayment,
      sendingCode,
      setConsents,
      setError,
      setErrorDetail,
      setAnonymousCheckout,
      setScreen,
      store,
      setSelectedPayment,
      handleSubmitWithValidation,
      userPhone,
      handleVerifyCode,
      verifyingCode,
    ]
  );

  return (
    <CheckoutContext.Provider value={context}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => useContextEnsuringProvider(CheckoutContext);
